import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { BiX } from 'react-icons/bi';
import swal from 'sweetalert2';
import Loading from '../common/Loading';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1500;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    width: 92vw;
    padding: 20px;
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 20px; right: 20px;
  cursor: pointer;
`;

const Title = styled.h3`
  margin-bottom: 20px;
  font-size: 22px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6e6e73;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #007aff; }
`;

const AliasWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const AliasTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
`;

const RemoveAlias = styled.span`
  cursor: pointer;
  color: #9ca3af;
  font-weight: 700;
  &:hover { color: #ef4444; }
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  background: ${({ $variant }) => $variant === 'primary' ? '#007aff' : '#f3f4f6'};
  color: ${({ $variant }) => $variant === 'primary' ? '#fff' : '#374151'};
  &:hover { opacity: 0.85; }
`;

const UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 8px 10px;
    text-align: center;
    border-bottom: 1px solid #eee;
    font-size: 13px;
  }
  thead tr { background: #2d3748; }
  th { color: #fff !important; }
`;

const CompanyDetailModal = ({ companyId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [officialName, setOfficialName] = useState('');
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState('');
  const [dirty, setDirty] = useState(false);
  const [memo, setMemo] = useState('');
  const [memoSaved, setMemoSaved] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: res } = await axios.get(`/admin/company/detail/${companyId}`);
        setData(res);
        setName(res.company.name);
        setOfficialName(res.company.official_name || '');
        setAliases(res.company.aliases || []);
        setMemo(res.company.admin_memo || '');
        setMemoSaved(res.company.admin_memo || '');
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [companyId]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleAddAlias = () => {
    const val = newAlias.trim();
    if (!val) return;
    if (aliases.includes(val)) {
      swal.fire({ text: '이미 존재하는 별칭입니다.', icon: 'warning', showConfirmButton: false, timer: 1000 });
      return;
    }
    setAliases([...aliases, val]);
    setNewAlias('');
    setDirty(true);
  };

  const handleRemoveAlias = (alias) => {
    setAliases(aliases.filter((a) => a !== alias));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      swal.fire({ text: '회사명을 입력해주세요.', icon: 'warning', showConfirmButton: false, timer: 1000 });
      return;
    }
    try {
      await axios.patch(`/admin/company/edit/${companyId}`, {
        name: name.trim(),
        official_name: officialName.trim(),
        aliases,
      });
      swal.fire({ text: '저장되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
      setDirty(false);
      onClose();
    } catch (err) {
      swal.fire({ text: err.response?.data?.message || '오류 발생', icon: 'error', showConfirmButton: false, timer: 1500 });
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalBox>
        <CloseBtn onClick={onClose}><BiX size={28} /></CloseBtn>
        {loading ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading />
          </div>
        ) : data ? (
          <>
            <Title>회사 상세</Title>

            <Section>
              <Label>대표 회사명</Label>
              <Input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} />
            </Section>

            <Section>
              <Label>공식 법인명</Label>
              <Input value={officialName} onChange={(e) => { setOfficialName(e.target.value); setDirty(true); }} />
            </Section>

            <Section>
              <Label>별칭 (기존 company_name 매핑)</Label>
              <AliasWrap>
                {aliases.map((a, i) => (
                  <AliasTag key={i}>
                    {a}
                    <RemoveAlias onClick={() => handleRemoveAlias(a)}>&times;</RemoveAlias>
                  </AliasTag>
                ))}
              </AliasWrap>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <Input
                  style={{ width: 200 }}
                  placeholder="별칭 추가"
                  value={newAlias}
                  onChange={(e) => setNewAlias(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAlias(); } }}
                />
                <Btn onClick={handleAddAlias}>추가</Btn>
              </div>
            </Section>

            {dirty && (
              <div style={{ marginBottom: 20 }}>
                <Btn $variant="primary" onClick={handleSave}>저장</Btn>
              </div>
            )}

            <Section>
              <Label>소속 회원 ({data.users.length}명)</Label>
              {data.users.length > 0 ? (
                <UserTable>
                  <thead>
                    <tr>
                      <th>이메일</th>
                      <th>담당자</th>
                      <th>연락처</th>
                      <th>기존 회사명</th>
                      <th>등급</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u._id}>
                        <td>{u.user_email}</td>
                        <td>{u.manager_name}</td>
                        <td>{u.manager_contact}</td>
                        <td style={{ color: '#9ca3af', fontSize: 12 }}>{u.company_name}</td>
                        <td>{u.level}</td>
                      </tr>
                    ))}
                  </tbody>
                </UserTable>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: 14, padding: '20px 0' }}>소속 회원이 없습니다.</div>
              )}
            </Section>

            <Section>
              <Label>관리자 메모</Label>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="이 회사에 대한 관리자 메모를 입력하세요..."
                style={{
                  width: '100%', minHeight: 80, padding: '10px 12px',
                  border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14,
                  fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                  lineHeight: 1.6, marginTop: 6,
                }}
              />
              {memo !== memoSaved && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Btn $variant="primary" onClick={async () => {
                    try {
                      await axios.patch(`/admin/company/edit/${companyId}`, { admin_memo: memo });
                      setMemoSaved(memo);
                      swal.fire({ text: '메모가 저장되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                    } catch {
                      swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                    }
                  }}>메모 저장</Btn>
                </div>
              )}
            </Section>
          </>
        ) : (
          <div style={{ color: '#999', padding: '40px 0', textAlign: 'center' }}>데이터를 불러올 수 없습니다.</div>
        )}
      </ModalBox>
    </Overlay>
  );
};

export default CompanyDetailModal;
